import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell, FormShell, WorkspaceShell } from '@/components/layout';
import { ProtectedRoute } from '@/components/routing/ProtectedRoute';
import { PublicOnlyRoute } from '@/components/routing/PublicOnlyRoute';
import { HomePage } from '@/slices/home';
import { SuppliersPage } from '@/slices/suppliers';
import { LoginPage, SignupPage, ForgotPasswordPage, ResetPasswordPage } from '@/slices/auth';
import { TransactionsPage } from '@/slices/transactions';
import {
  ContractsPage,
  ContractDetailPage,
  AddContractPage,
  EditContractPage,
  ReceiveShipmentPage,
} from '@/slices/contracts';
import {
  AddDeclarationPage,
  DeclarationsPage,
  DeclarationDetailPage,
  ValidateDeclarationPage,
  EditDeclarationPage,
} from '@/slices/declarations';
import {
  IncomingShipmentsPage,
  ShipmentDetailPage,
  AddShipmentPage,
  EditShipmentPage,
} from '@/slices/incoming-shipments';
import {
  OrdersPage,
  OrderDetailPage,
  AddOrderPage,
  EditOrderPage,
  FulfillOrderPage,
} from '@/slices/orders';
import {
  OutgoingShipmentsPage,
  AddOutgoingShipmentPage,
  EditOutgoingShipmentPage,
  OutgoingShipmentDetailPage,
  FulfillShipmentPage,
} from '@/slices/outgoing-shipments';
import {
  AccountSetupPage,
  AddSitePage,
  SiteDetailPage,
  EditSitePage,
} from '@/slices/account-setup';
import {
  AllocationsPage,
  AllocationDetailPage,
  EditAllocationDeclarationPage,
} from '@/slices/allocations';
import { InventoryPage, InventoryDetailPage } from '@/slices/inventory';
import { AdminCentrePage } from '@/slices/admin';
import {
  CustomersListPage,
  CustomerDetailsPage,
  AddCustomerSitesPage,
  EditCustomerSitePage,
} from '@/slices/customers';
import { ROUTES, SEGMENTS } from './routes';

// Route tree — PublicOnlyRoute for auth screens, ProtectedRoute for everything else.
export const router = createBrowserRouter([
  {
    element: <PublicOnlyRoute />,
    children: [
      { path: ROUTES.login, element: <LoginPage /> },
      { path: ROUTES.signup, element: <SignupPage /> },
      { path: ROUTES.forgotPassword, element: <ForgotPasswordPage /> },
      { path: ROUTES.resetPassword, element: <ResetPasswordPage /> },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: ROUTES.root,
        element: <AppShell />,
        children: [
          { index: true, element: <HomePage /> },
          { path: ROUTES.admin, element: <AdminCentrePage /> },
          { path: ROUTES.activityLog, element: <AdminCentrePage /> },
          { path: 'customers', element: <CustomersListPage /> },
          { path: 'customers/:customerId', element: <CustomerDetailsPage /> },
          { path: 'suppliers', element: <SuppliersPage /> },
        ],
      },
      {
        element: <FormShell />,
        children: [
          { path: SEGMENTS.account, element: <AccountSetupPage /> },
          { path: `${SEGMENTS.account}/sites/new`, element: <AddSitePage /> },
          { path: `${SEGMENTS.account}/sites/:siteId`, element: <SiteDetailPage /> },
          { path: `${SEGMENTS.account}/sites/:siteId/edit`, element: <EditSitePage /> },
          { path: 'customers/:customerId/sites/new', element: <AddCustomerSitesPage /> },
          {
            path: 'customers/:customerId/sites/:siteId/edit',
            element: <EditCustomerSitePage />,
          },
        ],
      },
      {
        path: SEGMENTS.module,
        children: [
          {
            element: <WorkspaceShell />,
            children: [
              { index: true, element: <Navigate to={`${SEGMENTS.inbounds}/contracts`} replace /> },
              {
                path: SEGMENTS.inbounds,
                children: [
                  { index: true, element: <Navigate to="contracts" replace /> },
                  { path: 'contracts', element: <ContractsPage /> },
                  { path: 'contracts/:contractId', element: <ContractDetailPage /> },
                  { path: 'declarations', element: <DeclarationsPage /> },
                  { path: 'declarations/:declarationId', element: <DeclarationDetailPage /> },
                  { path: 'incoming-shipments', element: <IncomingShipmentsPage /> },
                  { path: 'incoming-shipments/:shipmentId', element: <ShipmentDetailPage /> },
                ],
              },
              {
                path: SEGMENTS.outbounds,
                children: [
                  { index: true, element: <Navigate to="orders" replace /> },
                  { path: 'orders', element: <OrdersPage /> },
                  { path: 'orders/:orderId', element: <OrderDetailPage /> },
                  { path: 'outgoing-shipments', element: <OutgoingShipmentsPage /> },
                  {
                    path: 'outgoing-shipments/:shipmentId',
                    element: <OutgoingShipmentDetailPage />,
                  },
                  { path: 'allocations', element: <AllocationsPage /> },
                  { path: 'allocations/:allocationId', element: <AllocationDetailPage /> },
                ],
              },
              {
                path: SEGMENTS.monitor,
                children: [
                  { index: true, element: <Navigate to="transactions" replace /> },
                  { path: 'transactions', element: <TransactionsPage /> },
                ],
              },
              {
                path: SEGMENTS.monitor,
                children: [
                  { index: true, element: <Navigate to="inventory" replace /> },
                  { path: 'inventory', element: <InventoryPage /> },
                  { path: 'inventory/:inventoryId', element: <InventoryDetailPage /> },
                ],
              },
            ],
          },
          {
            element: <FormShell />,
            children: [
              { path: `${SEGMENTS.inbounds}/contracts/new`, element: <AddContractPage /> },
              {
                path: `${SEGMENTS.inbounds}/contracts/:contractId/edit`,
                element: <EditContractPage />,
              },
              {
                path: `${SEGMENTS.inbounds}/contracts/:contractId/receive-shipment`,
                element: <ReceiveShipmentPage />,
              },
              {
                path: `${SEGMENTS.inbounds}/incoming-shipments/new`,
                element: <AddShipmentPage />,
              },
              {
                path: `${SEGMENTS.inbounds}/incoming-shipments/:shipmentId/edit`,
                element: <EditShipmentPage />,
              },
              {
                path: `${SEGMENTS.inbounds}/declarations/new`,
                element: <AddDeclarationPage />,
              },
              {
                path: `${SEGMENTS.inbounds}/incoming-shipments/:shipmentId/add-declarations`,
                element: <AddDeclarationPage />,
              },
              {
                path: `${SEGMENTS.outbounds}/orders/new`,
                element: <AddOrderPage />,
              },
              {
                path: `${SEGMENTS.outbounds}/orders/:orderId/edit`,
                element: <EditOrderPage />,
              },
              {
                path: `${SEGMENTS.outbounds}/outgoing-shipments/new`,
                element: <AddOutgoingShipmentPage />,
              },
              {
                path: `${SEGMENTS.outbounds}/outgoing-shipments/:shipmentId/edit`,
                element: <EditOutgoingShipmentPage />,
              },
              {
                path: `${SEGMENTS.outbounds}/orders/:orderId/fulfill`,
                element: <FulfillOrderPage />,
              },
              {
                path: `${SEGMENTS.outbounds}/outgoing-shipments/:shipmentId/fulfill`,
                element: <FulfillShipmentPage />,
              },
              {
                path: `${SEGMENTS.inbounds}/declarations/:declarationId/validate`,
                element: <ValidateDeclarationPage />,
              },
              {
                path: `${SEGMENTS.inbounds}/declarations/:declarationId/edit`,
                element: <EditDeclarationPage />,
              },
              {
                path: `${SEGMENTS.outbounds}/allocations/:allocationId/edit-declaration`,
                element: <EditAllocationDeclarationPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  { path: ROUTES.notFound, element: <Navigate to={ROUTES.root} replace /> },
]);
